import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import toast from 'react-hot-toast'
import type { KeyedMutator } from 'swr'
import { PrimaryButton } from 'shared/components/button'
import Modal from 'shared/components/modal'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { cancelSubscription } from '../api/cancel-subscriptions-api'
import type {
  ManageSubscriptionsApiInterface,
  ManageSubscriptionsInterface,
} from '../types/manage-subscriptions-interface'

interface CancelSubscriptionModalProps {
  opened: boolean
  onClose: () => void
  subscription: ManageSubscriptionsInterface
  mutate: KeyedMutator<ManageSubscriptionsApiInterface>
}

function CancelSubscriptionModal({
  opened,
  onClose,
  subscription,
  mutate,
}: CancelSubscriptionModalProps): ReactNode {
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const [isFetching, setIsFetching] = useState(false)

  const handleConfirm = async (): Promise<void> => {
    try {
      setIsFetching(true)
      await cancelSubscription(
        subscription.id,
        subscription.subscriptionItems[0].id,
        subscription.paymentProcessor,
      )
      await mutate(data => {
        if (data) {
          return {
            items: data.items.map(item => {
              if (item.id === subscription.id) item = { ...item, status: 'cancelled' }
              return item
            }),
            hasMore: data.hasMore,
          }
        }
      }, false)
      setIsFetching(false)
      onClose()
      toast.success(t('profile.subscriptions.unsubscribed'))
    } catch (error_) {
      setIsFetching(false)
      if (error_ instanceof BadRequest) {
        if (error_.errors.common) {
          setError(error_.errors.common.join(''))
        }
      } else if (error_ instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toast.error(t('core.error.internal_error_message'))
      }
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('confirmation_modal.default_title')}
      small
      isFetching={isFetching}
    >
      <form className="flex flex-col gap-6 lg:gap-10">
        <div className="flex justify-center">
          <p>{t('profile.subscriptions.unsubscribe_confirmation')}</p>
        </div>
        <div className="flex justify-center gap-4">
          <PrimaryButton
            isLoading={isFetching}
            onClick={async e => {
              e.preventDefault()
              await handleConfirm()
            }}
            type="submit"
          >
            {t('confirmation_modal.confirm')}
          </PrimaryButton>
          <PrimaryButton
            onClick={e => {
              e.preventDefault()
              onClose()
            }}
            disabled={isFetching}
          >
            {t('confirmation_modal.cancel')}
          </PrimaryButton>
        </div>
        {error && <p className="text-center text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  )
}

export default CancelSubscriptionModal
